import { animated, config, useSpring } from "@react-spring/web";
import { App, Button, Dropdown, Input, Typography, theme } from "antd";
import { isHandoffOpen, latestStaffName } from "hooks/chatHandoffState";
import { useChatAccess } from "hooks/useChatAccess";
import { useChatPageContext } from "hooks/useChatPageContext";
import { useCompanies } from "hooks/useCompanies";
import { useUserChat } from "hooks/useUserChat";
import { Sparkles, Square, X } from "lucide-react";
import type React from "react";
import { useEffect, useRef, useState } from "react";
import { useMatch, useNavigate } from "react-router-dom";
import type { IChatActionProposal } from "services/chatActionsApi";
import { ChatInput } from "./components/ChatInput";
import { ChatMessage } from "./components/ChatMessage";
import {
	HandoffBanner,
	SessionEndedNotice,
	TalkToPersonButton,
} from "./components/HandoffControls";
import { resolveNavigation } from "./resolveNavigation";
import "./styles.css";

const { Title, Text } = Typography;

// Concrete data-Q&A starters, shown only when a company is in context. Ported
// from the retired Assistant drawer (DataChatPanel) so deleting that surface
// doesn't delete the only place data starters lived (Decision Audit #12).
const DATA_STARTER_PROMPTS = [
	"How many Stripe transactions were under $2.00 in 2023?",
	"What's the total of all deposits last month?",
	"List the 10 most recent transactions over $5,000.",
];

// Navigation/feature starters, always available.
const GENERAL_STARTER_PROMPTS = [
	"Navigate to Dashboard",
	"Show me analytics",
	"Help",
];

export function ChatBar() {
	const { isChatDisabled } = useChatAccess();
	const navigate = useNavigate();
	const { message } = App.useApp();
	const { token } = theme.useToken();
	// Resolve the current company from the route (authoritative — the persisted
	// company store can be stale on a deep link). Validated against the
	// accessible companies list so a stale/forbidden route id doesn't flow into
	// the data-token mint. When present the backend can mint the data service
	// token and the SESSION agent answers transaction-data questions.
	const companyRouteMatch = useMatch("/company/:id/*");
	const routeCompanyId = companyRouteMatch?.params.id
		? Number(companyRouteMatch.params.id)
		: null;
	const { data: companies } = useCompanies();
	// A close's project address names no company; its page supplies the
	// close's client along with the hint that it is a close.
	const { pageContext, closeCompanyId } = useChatPageContext();
	const pageCompanyId = routeCompanyId ?? closeCompanyId;
	const currentCompanyId =
		pageCompanyId && companies?.some((c) => c.id === pageCompanyId)
			? pageCompanyId
			: null;
	const hasCompanyContext = currentCompanyId !== null;
	const [isOpen, setIsOpen] = useState(false);
	const [isHovered, setIsHovered] = useState(false);
	const [triggerInputValue, setTriggerInputValue] = useState("");
	const messagesEndRef = useRef<HTMLDivElement>(null);

	const {
		messages,
		isLoading,
		isStreaming,
		isHealthy,
		sendMessageStream,
		appendActionProposal,
		stopStreaming,
		clearConversation,
		handoff,
		isRequestingHuman,
		requestHuman,
		cancelHuman,
		handoffError,
		isSessionEnded,
	} = useUserChat(currentCompanyId);
	const hasOpenHandoff = isHandoffOpen(handoff);

	// Animations
	const chatAnimation = useSpring({
		opacity: isOpen ? 1 : 0,
		transform: isOpen
			? "translateY(0) scale(1)"
			: "translateY(-10px) scale(0.98)",
		config: config.stiff,
	});

	// Auto-scroll
	useEffect(() => {
		if (isOpen) {
			setTimeout(() => {
				messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
			}, 100);
		}
	}, [messages, isOpen]);

	// Bridge: another surface (e.g. the reconciliation results-page "Propose for
	// approval" button) proposes an action to the backend, then dispatches this
	// window event with the returned proposal. Open the assistant and drop the
	// confirm-card into the transcript so the user can review + approve it here.
	useEffect(() => {
		const handleProposeEvent = (event: Event) => {
			const detail = (event as CustomEvent<IChatActionProposal>).detail;
			if (!detail) return;
			setIsOpen(true);
			appendActionProposal(detail);
		};
		window.addEventListener("equility:chat:propose", handleProposeEvent);
		return () =>
			window.removeEventListener("equility:chat:propose", handleProposeEvent);
	}, [appendActionProposal]);

	// If chat is disabled for this user, don't render anything
	if (isChatDisabled) {
		return null;
	}

	// Returns whether the send was accepted, so the input can clear on accept and
	// restore the text when it was dropped. Navigation/offline both resolve
	// synchronously; a real send delegates to the hook's guard (which returns
	// false when busy instead of silently swallowing the message).
	const handleSendMessage = async (text: string): Promise<boolean> => {
		// Navigation intent is handled client-side: the assistant has no
		// navigation tool, so "go to X" phrases are resolved to a route and drive
		// react-router directly instead of reaching the LLM (which otherwise
		// hallucinates fake URLs and poisons the session). Data questions fall
		// through to the LLM unchanged.
		const nav = resolveNavigation(text, currentCompanyId);
		if (nav) {
			if (nav.kind === "navigate") {
				navigate(nav.route);
				setIsOpen(false);
			} else {
				message.info(`Open a company first to view its ${nav.label}.`);
			}
			return true;
		}

		if (!isHealthy) {
			message.warning("The assistant is offline right now.");
			return false;
		}

		try {
			// Add context about the current page
			return await sendMessageStream(`${pageContext} ${text}`);
		} catch (error) {
			console.error("Chat error:", error);
			// The user already saw transcript/toast feedback from the hook; treat
			// the attempt as accepted so the input clears rather than duplicating.
			return true;
		}
	};

	// Same contract as ChatInput.handleSend: clear optimistically so the box
	// stays snappy, then RESTORE the text if the send was dropped (assistant
	// offline, a turn already streaming). Clearing unconditionally threw the
	// user's question away behind a toast, leaving them to retype it.
	// Don't await before clearing — a real send resolves only when the SSE
	// stream closes, so that would freeze the input for the whole turn.
	const handleTriggerInputSubmit = () => {
		const text = triggerInputValue.trim();
		if (!text) return;
		setIsOpen(true);
		setTriggerInputValue("");
		void Promise.resolve(handleSendMessage(text)).then((accepted) => {
			if (accepted === false) {
				setTriggerInputValue(text);
			}
		});
	};

	const handleTriggerKeyDown = (e: React.KeyboardEvent) => {
		if (e.key === "Enter" && triggerInputValue.trim()) {
			e.preventDefault();
			handleTriggerInputSubmit();
		}
	};

	// Empty-state starters, grouped by intent so the pile reads as two labeled
	// choices ("Your data" vs "Go somewhere") instead of an undifferentiated wall
	// of chips. Data starters only appear when a company is in context.
	const starterGroups = [
		...(hasCompanyContext
			? [
					{
						label: "Your data",
						dotColor: token.colorPrimary,
						prompts: DATA_STARTER_PROMPTS,
					},
				]
			: []),
		{
			label: "Go somewhere",
			dotColor: token.colorInfo,
			prompts: GENERAL_STARTER_PROMPTS,
		},
	];

	const chatContent = (
		<animated.div
			style={{
				...(chatAnimation as any),
				width: 600,
				height: 660,
				display: "flex",
				flexDirection: "column",
				borderRadius: 16,
				overflow: "hidden",
				boxShadow:
					"0 18px 50px rgba(13,26,19,0.16), 0 4px 12px rgba(13,26,19,0.08)",
				pointerEvents: "auto",
			}}
			className="chat-glass-panel"
		>
			{/* Header */}
			<div
				style={{
					padding: "16px 20px",
					borderBottom: `1px solid ${token.colorBorderSecondary}`,
					background: "rgba(255,255,255,0.5)",
					backdropFilter: "blur(10px)",
					display: "flex",
					justifyContent: "space-between",
					alignItems: "center",
				}}
			>
				<div style={{ display: "flex", alignItems: "center", gap: 8 }}>
					<div
						style={{
							width: 32,
							height: 32,
							borderRadius: 8,
							background: `linear-gradient(150deg, ${token.colorPrimary} 0%, var(--color-primary-hover) 100%)`,
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
							color: "#fff",
						}}
					>
						<Sparkles size={18} />
					</div>
					<div>
						<Title level={5} style={{ margin: 0, fontSize: 16 }}>
							AI Assistant
						</Title>
						<div style={{ display: "flex", alignItems: "center", gap: 4 }}>
							<div
								style={{
									width: 6,
									height: 6,
									borderRadius: "50%",
									background: isHealthy
										? "var(--color-success)"
										: "var(--color-error)",
								}}
							/>
							<Text type="secondary" style={{ fontSize: 11 }}>
								{isHealthy ? "Online" : "Offline"}
							</Text>
						</div>
					</div>
				</div>

				<div className="chat-header-actions">
					<TalkToPersonButton
						handoff={handoff}
						isRequesting={isRequestingHuman}
						onRequest={() => void requestHuman()}
						// The chat session only exists upstream after the first
						// message, and a handoff needs one to attach to.
						isDisabled={!messages.some((m) => m.role === "user")}
						disabledReason="Send a message first"
					/>
					{/* Clearing rotates the session, which would strand an open
					    request for a person on a chat the customer can no longer see. */}
					<Button
						type="text"
						size="small"
						onClick={clearConversation}
						disabled={messages.length === 0 || hasOpenHandoff}
						style={{ fontSize: 12 }}
					>
						Clear
					</Button>
					<Button
						type="text"
						size="small"
						icon={<X size={16} />}
						aria-label="Close chat"
						onClick={() => setIsOpen(false)}
						style={{
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
							padding: 4,
						}}
					/>
				</div>
			</div>

			<HandoffBanner
				handoff={handoff}
				onCancel={() => void cancelHuman()}
				lastStaffName={latestStaffName(messages)}
				error={handoffError}
			/>

			{/* Messages Area */}
			<div
				className="chat-scroll-area"
				role="log"
				aria-live="polite"
				aria-label="Chat transcript"
				style={{
					flex: 1,
					overflowY: "auto",
					padding: 20,
					display: "flex",
					flexDirection: "column",
				}}
			>
				{messages.length === 0 ? (
					<div
						style={{
							flex: 1,
							display: "flex",
							flexDirection: "column",
							alignItems: "center",
							paddingTop: 28,
						}}
					>
						<div
							style={{
								width: 60,
								height: 60,
								borderRadius: 16,
								background: `linear-gradient(150deg, ${token.colorPrimary} 0%, var(--color-primary-hover) 100%)`,
								display: "flex",
								alignItems: "center",
								justifyContent: "center",
								color: "#fff",
								boxShadow: "0 8px 24px rgba(15,150,72,0.32)",
								marginBottom: 16,
							}}
						>
							<Sparkles size={26} strokeWidth={1.8} />
						</div>
						<Title level={5} style={{ margin: 0, fontSize: 18 }}>
							How can I help?
						</Title>
						<Text
							type="secondary"
							style={{
								fontSize: 13,
								textAlign: "center",
								marginTop: 4,
								maxWidth: 320,
							}}
						>
							{hasCompanyContext
								? "Ask about your transactions, or tell me where you want to go."
								: "Ask about navigation, features, or how things work."}
						</Text>

						<div style={{ width: "100%", maxWidth: 380, marginTop: 24 }}>
							{starterGroups.map((group) => (
								<div key={group.label} style={{ marginBottom: 14 }}>
									<div
										style={{
											display: "flex",
											alignItems: "center",
											gap: 6,
											marginBottom: 8,
											paddingLeft: 2,
										}}
									>
										<span
											style={{
												width: 5,
												height: 5,
												borderRadius: "50%",
												background: group.dotColor,
											}}
										/>
										<Text
											style={{
												fontSize: 10.5,
												fontWeight: 700,
												letterSpacing: "0.05em",
												textTransform: "uppercase",
												color: token.colorTextTertiary,
											}}
										>
											{group.label}
										</Text>
									</div>
									<div
										style={{
											display: "flex",
											flexDirection: "column",
											gap: 7,
										}}
									>
										{group.prompts.map((prompt) => (
											<button
												key={prompt}
												type="button"
												className="chat-starter"
												onClick={() => handleSendMessage(prompt)}
											>
												<span>{prompt}</span>
												<span className="chat-starter-arrow">→</span>
											</button>
										))}
									</div>
								</div>
							))}
						</div>
					</div>
				) : (
					<>
						{messages.map((msg, index) => {
							const isLastMessage = index === messages.length - 1;
							const isEmptyStreamingMessage =
								isStreaming &&
								isLastMessage &&
								msg.role === "assistant" &&
								!msg.content;

							// Show typing indicator instead of empty message bubble
							if (isEmptyStreamingMessage) {
								return (
									<div
										key={index}
										style={{
											display: "flex",
											gap: 12,
											marginBottom: 16,
											alignItems: "flex-start",
											padding: "0 4px",
										}}
									>
										<div
											style={{
												width: 32,
												height: 32,
												borderRadius: "50%",
												backgroundColor: token.colorBgContainer,
												border: `1px solid ${token.colorBorder}`,
												display: "flex",
												alignItems: "center",
												justifyContent: "center",
												flexShrink: 0,
												boxShadow: token.boxShadowTertiary,
											}}
										>
											<Sparkles size={16} color={token.colorPrimary} />
										</div>
										<div
											style={{
												padding: "12px 16px",
												borderRadius: "16px 16px 16px 4px",
												background: token.colorBgContainer,
												border: `1px solid ${token.colorBorderSecondary}`,
												boxShadow: token.boxShadowTertiary,
												display: "flex",
												alignItems: "center",
												gap: 8,
											}}
										>
											<div style={{ display: "flex", gap: 4 }}>
												<div
													className="typing-dot"
													style={{
														width: 6,
														height: 6,
														background: token.colorPrimary,
														borderRadius: "50%",
													}}
												/>
												<div
													className="typing-dot"
													style={{
														width: 6,
														height: 6,
														background: token.colorPrimary,
														borderRadius: "50%",
													}}
												/>
												<div
													className="typing-dot"
													style={{
														width: 6,
														height: 6,
														background: token.colorPrimary,
														borderRadius: "50%",
													}}
												/>
											</div>
											<Text type="secondary" style={{ fontSize: 13 }}>
												Thinking...
											</Text>
										</div>
									</div>
								);
							}

							return (
								<ChatMessage
									key={index}
									role={msg.role}
									content={
										typeof msg.content === "string"
											? msg.content
											: JSON.stringify(msg.content)
									}
									timestamp={msg.timestamp}
									isError={msg.isError}
									blocks={msg.blocks}
									metadata={msg.metadata}
									staffName={msg.staffName}
									kind={msg.kind}
								/>
							);
						})}
						{isStreaming &&
							messages.length > 0 &&
							messages[messages.length - 1].content && (
								<div
									style={{
										display: "flex",
										alignItems: "center",
										gap: 8,
										padding: "10px 14px",
									}}
								>
									<Button
										size="small"
										type="text"
										danger
										icon={<Square size={12} fill="currentColor" />}
										onClick={stopStreaming}
										style={{ fontSize: 12 }}
									>
										Stop
									</Button>
									<Text type="secondary" style={{ fontSize: 12 }}>
										Generating...
									</Text>
								</div>
							)}
						{isLoading && !isStreaming && (
							<div style={{ display: "flex", gap: 4, padding: "10px 14px" }}>
								<div
									className="typing-dot"
									style={{
										width: 6,
										height: 6,
										background: token.colorTextSecondary,
										borderRadius: "50%",
									}}
								/>
								<div
									className="typing-dot"
									style={{
										width: 6,
										height: 6,
										background: token.colorTextSecondary,
										borderRadius: "50%",
									}}
								/>
								<div
									className="typing-dot"
									style={{
										width: 6,
										height: 6,
										background: token.colorTextSecondary,
										borderRadius: "50%",
									}}
								/>
							</div>
						)}
						{isSessionEnded && (
							<SessionEndedNotice onNewChat={clearConversation} />
						)}
						<div ref={messagesEndRef} />
					</>
				)}
			</div>

			{/* Input Area */}
			<ChatInput
				onSend={handleSendMessage}
				isLoading={isLoading || isStreaming}
				disabled={!isHealthy || isStreaming || isSessionEnded}
				autoFocus={isOpen}
			/>
		</animated.div>
	);

	return (
		<div className="chat-bar-header-slot">
			<Dropdown
				open={isOpen}
				onOpenChange={(open) => {
					// Only allow closing via this handler, not opening
					if (!open) setIsOpen(false);
				}}
				dropdownRender={() => chatContent}
				trigger={[]}
				placement="bottomRight"
				getPopupContainer={() => document.body}
				overlayStyle={{ zIndex: 9999 }}
			>
				<div
					onMouseEnter={() => setIsHovered(true)}
					onMouseLeave={() => setIsHovered(false)}
					className="chat-bar-header-trigger"
				>
					<Input
						placeholder={
							messages.length > 0 ? "Continue in chat..." : "Ask AI anything..."
						}
						prefix={
							<Sparkles
								size={16}
								color={isHovered || isOpen ? "var(--color-info)" : "#999"}
								style={{ cursor: "pointer" }}
								onClick={() => setIsOpen(true)}
							/>
						}
						value={triggerInputValue}
						onChange={(e) => {
							// Only allow typing when there are no existing messages
							if (messages.length === 0) {
								setTriggerInputValue(e.target.value);
							}
						}}
						onKeyDown={handleTriggerKeyDown}
						readOnly={messages.length > 0}
						onClick={() => setIsOpen(true)}
						style={{
							borderRadius: 20,
							background: isOpen ? "var(--color-info-light)" : "#fff",
							borderColor: isOpen ? "var(--color-info)" : undefined,
							cursor: messages.length > 0 ? "pointer" : "text",
							transition: "all 0.3s ease",
						}}
					/>
				</div>
			</Dropdown>
		</div>
	);
}

export default ChatBar;
